import { ChangeDetectionStrategy, Component, OnInit } from '@angular/core';
import { MatDialogRef } from '@angular/material/dialog';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { FeatureManagementService } from 'src/app/services/feature-management.service';
import { tap } from 'rxjs/operators';
import { BehaviorSubject } from 'rxjs';
import {
  SpacesTemplatesCategories,
  SpacesTemplatesService,
} from '../../services/spaces-templates.service';
import { UserService } from '../../services/user.service';
import { ISpaceTemplate } from '../../models/session';
import { NetworkService } from '../../services/network.service';
@UntilDestroy()
@Component({
  selector: 'app-choose-space-template-dialog',
  templateUrl: './choose-space-template-dialog.component.html',
  styleUrls: ['./choose-space-template-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ChooseSpaceTemplateDialogComponent implements OnInit {
  isInstitutionTemplatesEnabled$ =
    this.featureManagementService.isInstitutionTemplatesEnabled$.pipe(
      tap((isEnabled) => {
        // set initial category state
        this.selectedCategory = isEnabled
          ? SpacesTemplatesCategories.INSTITUTION
          : SpacesTemplatesCategories.GENERAL;
      }),
    );

  availableTemplates: ISpaceTemplate[] = [];
  currentDisplayedSpacesTemplates$ = new BehaviorSubject<ISpaceTemplate[]>([]);
  uiTemplates: ISpaceTemplate[] = [];
  selectedCategory = SpacesTemplatesCategories.INSTITUTION;
  templatesLoaded = false;

  constructor(
    private dialogRef: MatDialogRef<ChooseSpaceTemplateDialogComponent>,
    protected spacesTemplatesService: SpacesTemplatesService,
    protected userService: UserService,
    private networkService: NetworkService,
    private featureManagementService: FeatureManagementService,
  ) {}

  ngOnInit(): void {
    this.loadTemplates();

    this.networkService.networkReconnected$.pipe(untilDestroyed(this)).subscribe(() => {
      if (!this.templatesLoaded) {
        this.loadTemplates();
      }
    });
  }

  loadTemplates() {
    this.spacesTemplatesService
      .getAllSpacesTemplates()
      .pipe(untilDestroyed(this))
      .subscribe((value) => {
        this.templatesLoaded = true;
        this.availableTemplates = value;
        this.filterTemplatesBasedOnCurrentTab();
      });
  }

  close() {
    this.dialogRef.close();
  }

  filterTemplatesBasedOnCurrentTab() {
    if (this.selectedCategory === SpacesTemplatesCategories.INSTITUTION) {
      this.uiTemplates = this.availableTemplates.filter(
        (template) => template.institution && !template.isGeneral,
      );
    } else {
      this.uiTemplates = this.availableTemplates.filter((template) => template.isGeneral);
    }

    this.currentDisplayedSpacesTemplates$.next(this.uiTemplates);
  }

  /**
   * Handles tab selection changes in template categories
   *
   * Important behavior notes:
   * - When institution templates capability is disabled:
   *   - isInstitutionTemplatesEnabled$ will be false
   *   - Only the General tab will be rendered in the UI
   *   - This function won't be called at all because tab selection isn't possible with only one tab
   *   - BE will only return general templates for users without the capability
   *
   * - When institution templates capability is enabled:
   *   - Tab index 0 corresponds to the Institution tab
   *   - Tab index 1 corresponds to the General tab
   *
   * @param tabIndex The index of the selected tab
   */
  selectedTabChanged(tabIndex: number) {
    if (tabIndex === 0) {
      this.selectedCategory = SpacesTemplatesCategories.INSTITUTION;
    } else {
      this.selectedCategory = SpacesTemplatesCategories.GENERAL;
    }

    this.filterTemplatesBasedOnCurrentTab();
  }

  filterTemplates(value: string) {
    this.currentDisplayedSpacesTemplates$.next(
      this.uiTemplates.filter((template) =>
        template.name.toLowerCase().includes(value.toLowerCase()),
      ),
    );
  }

  handleTemplateClicked(template?: ISpaceTemplate) {
    this.dialogRef.close({
      isTemplateClicked: true,
      template,
    });
  }
}
